// src/widgets/TableReservation/hooks/useReservationSteps.js
import {useState, useCallback} from 'react';

// Définir clairement les noms des étapes pour éviter les erreurs de string
export const STEPS = {
  SERVICE_GUESTS: 0, // Fusion des étapes SERVICE et GUESTS
  DATE: 1,
  TIME: 2,
  CONTACT: 3,
  DETAILS: 4,
  CONFIRMATION: 5,
};

export function useReservationSteps() {
  const [currentStep, setCurrentStep] = useState(STEPS.SERVICE_GUESTS);
  const [stepValidation, setStepValidation] = useState(() => () => true);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    service: '',
    partySize: 0,
    date: null,
    timeSlot: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    occasion: '',
  });

  // Fonction pour mettre à jour les données du formulaire
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Effacer les erreurs associées à ce champ
    setValidationErrors((prev) => {
      const newErrors = {...prev};
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Navigation vers l'étape suivante
  const goToNextStep = useCallback(() => {
    // Valider l'étape actuelle avant de passer à la suivante
    if (stepValidation()) {
      setCurrentStep((prev) => prev + 1);
      // Réinitialiser les erreurs de validation lors du changement d'étape
      setValidationErrors({});
      return true;
    }
    return false;
  }, [stepValidation]);

  // Navigation vers l'étape précédente (pas besoin de validation)
  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
    // Réinitialiser les erreurs de validation lors du changement d'étape
    setValidationErrors({});
  }, []);

  // Aller à une étape spécifique
  const goToStep = useCallback((step) => {
    setCurrentStep(step);
    // Réinitialiser les erreurs de validation lors du changement d'étape
    setValidationErrors({});
  }, []);

  // Réinitialiser complètement le formulaire
  const resetSteps = useCallback(() => {
    setCurrentStep(STEPS.SERVICE_GUESTS);
    setFormData({
      service: '',
      partySize: 0,
      date: null,
      timeSlot: null,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      specialRequests: '',
      occasion: '',
    });
    setValidationErrors({});
  }, []);

  // Mise à jour de la fonction de validation depuis les composants enfants
  const setValidationFunction = useCallback((validationFn) => {
    setStepValidation(() => validationFn);
  }, []);

  // Définir des erreurs de validation
  const setErrors = useCallback((errors) => {
    setValidationErrors(errors);
  }, []);

  return {
    currentStep,
    formData,
    validationErrors,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetSteps,
    setValidationFunction,
    setErrors,
    STEPS,
  };
}
